import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { SocialMediaBar } from "../../components/SocialMediaBar";
import { 
  CreditCard, Calendar, Clock, AlertTriangle, CheckCircle, XCircle, BookOpen, 
  Calculator, MessageCircle, Youtube, Facebook, Instagram, ExternalLink, Download, Globe
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const BOOKING_TYPE_LABELS = {
  bq_abo: "BQ-Abonnement",
  hq_abo: "HQ-Abonnement",
  bq_intensiv: "BQ-Intensivkurs",
  hq_intensiv: "HQ-Intensivkurs",
  probetraining: "Probetraining",
  mathe_grundkurs: "Mathe-Grundkurs",
  fachgespraech: "Fachgespräch"
};

const HQ_SUB_LABELS = {
  metall: "HQ Abo Metalltechnik"
};

const StatusBadge = ({ status }) => {
  const config = {
    active: { class: "status-active", label: "Aktiv", icon: CheckCircle },
    expiring_soon: { class: "status-warning", label: "Läuft bald ab", icon: AlertTriangle },
    expired: { class: "status-expired", label: "Abgelaufen", icon: XCircle },
    scheduled: { class: "bg-blue-500/15 text-blue-500 border-blue-500/50 border", label: "Geplant", icon: Calendar }
  };
  
  const { class: className, label, icon: Icon } = config[status] || config.expired;
  
  return (
    <Badge className={`${className} rounded-sm text-xs font-medium flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
};

const BookingIcon = ({ type }) => {
  const icons = {
    bq_abo: CreditCard,
    hq_abo: CreditCard,
    bq_intensiv: BookOpen,
    hq_intensiv: BookOpen,
    probetraining: Calendar,
    mathe_grundkurs: Calculator,
    fachgespraech: MessageCircle
  };
  const Icon = icons[type] || CreditCard;
  return <Icon className="h-5 w-5 text-primary" />;
};

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const [socialLinks, setSocialLinks] = useState({});
  const [tick, setTick] = useState(0);

  const bookings = user?.bookings || [];
  const activeBookings = bookings.filter(b => b.access_allowed);
  const hasWarning = bookings.some(b => b.status === "expiring_soon");

  useEffect(() => {
    loadExtraData();
  }, []);

  // Auto-refresh user data (and thus days_remaining) every 60 seconds
  // and force a re-render every 30 seconds for client-side countdown.
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      refreshUser();
    }, 60000);
    const tickInterval = setInterval(() => setTick(t => t + 1), 30000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(tickInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute live days remaining client-side for instant feedback between refreshes
  const computeLiveDays = (booking) => {
    if (!booking?.end_date) return booking?.days_remaining;
    try {
      const end = new Date(booking.end_date + "T23:59:59Z");
      const now = new Date();
      const ms = end - now;
      const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
      return days;
    } catch (e) {
      return booking.days_remaining;
    }
  };
  // tick is referenced so React re-renders when interval fires
  void tick;

  const loadExtraData = async () => {
    try {
      const linksRes = await axios.get(`${API}/settings/social-links`);
      setSocialLinks(linksRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  return (
    <ParticipantLayout title="Mein Dashboard">
      <div className="space-y-6" data-testid="participant-dashboard">
        {/* Tabs: Social Media + Google Kalender (like admin Settings) */}
        <Tabs defaultValue="social" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 bg-secondary/50">
            <TabsTrigger
              value="social"
              data-testid="tab-social"
              className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Globe className="h-4 w-4" />
              Social Media
            </TabsTrigger>
            <TabsTrigger
              value="calendar"
              data-testid="tab-calendar"
              className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <Calendar className="h-4 w-4" />
              Google Kalender
            </TabsTrigger>
          </TabsList>

          <TabsContent value="social" className="space-y-4">
            <SocialMediaBar />
          </TabsContent>

          <TabsContent value="calendar" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Kalender & Termine
                </CardTitle>
              </CardHeader>
              <CardContent>
                {socialLinks.google_calendar_embed ? (
                  <>
                    <div className="border border-border rounded-lg overflow-hidden bg-white">
                      <iframe
                        src={
                          socialLinks.google_calendar_embed.includes('calendar.google.com/calendar/embed')
                            ? socialLinks.google_calendar_embed
                            : socialLinks.google_calendar_embed.includes('@')
                              ? `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(socialLinks.google_calendar_embed)}&ctz=Europe/Berlin`
                              : socialLinks.google_calendar_embed
                        }
                        style={{ border: 0 }}
                        width="100%"
                        height="500"
                        frameBorder="0"
                        scrolling="no"
                        title="Unterrichtskalender"
                      />
                    </div>
                    {socialLinks.google_calendar_embed.includes('.ics') && (
                      <div className="mt-3">
                        <Button
                          variant="outline"
                          onClick={() => window.open(socialLinks.google_calendar_embed, '_blank')}
                          className="gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Kalender herunterladen (ICS)
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-10 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Kalender wird bald hinzugefügt</p>
                    <p className="text-sm mt-1">Termine werden hier angezeigt</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Logo & Welcome Card */}
        <Card className="bg-card border-border overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {/* S&T Logo */}
              <div className="flex-shrink-0">
                <img 
                  src="/logo-st.webp" 
                  alt="S&T Brain-Trainer" 
                  className="h-20 w-auto object-contain"
                  data-testid="st-logo"
                />
              </div>
              
              {/* Welcome Text */}
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-1">Willkommen, {user?.name}!</h2>
                <p className="text-muted-foreground">
                  {user?.industriemeister_fachrichtung && (
                    <span>Fachrichtung: {user.industriemeister_fachrichtung}</span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Sie haben {bookings.length} Buchung{bookings.length !== 1 ? "en" : ""} 
                  ({activeBookings.length} aktiv)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Google Calendar moved into Tabs above */}

        {/* Warning Banner */}
        {hasWarning && (
          <Card className="bg-yellow-500/10 border-yellow-500/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="font-medium text-yellow-500">Abo läuft bald ab!</p>
                  <p className="text-sm text-muted-foreground">
                    Mindestens eines Ihrer Abonnements läuft in den nächsten 5 Tagen ab.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bookings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bookings.map((booking, index) => (
            <Card 
              key={booking.id || index} 
              className={`bg-card border-border ${!booking.access_allowed ? 'opacity-60' : ''}`}
              data-testid={`booking-card-${booking.booking_type}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookingIcon type={booking.booking_type} />
                    <CardTitle className="text-lg">
                      {BOOKING_TYPE_LABELS[booking.booking_type] || booking.booking_type}
                    </CardTitle>
                  </div>
                  <StatusBadge status={booking.status} />
                </div>
                {booking.hq_sub_type && (
                  <p className="text-sm text-primary mt-1">
                    {HQ_SUB_LABELS[booking.hq_sub_type]}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Booking details for all types except probetraining */}
                {booking.booking_type !== "probetraining" && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Startdatum</span>
                      <span className="font-mono">{booking.start_date}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Enddatum</span>
                      <span className="font-mono">
                        {booking.is_unlimited ? (
                          <span className="text-green-500">Unbegrenzt</span>
                        ) : (
                          booking.end_date
                        )}
                      </span>
                    </div>
                    {/* Show subjects for intensiv courses */}
                    {(booking.booking_type === "bq_intensiv" || booking.booking_type === "hq_intensiv") && booking.subjects && booking.subjects.length > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Fächer</span>
                        <span className="font-medium text-right">{booking.subjects.join(", ")}</span>
                      </div>
                    )}
                    {booking.days_remaining !== null && booking.days_remaining !== undefined && (
                      <div className={`p-3 rounded-sm ${
                        booking.status === "active" ? "bg-green-500/10" :
                        booking.status === "expiring_soon" ? "bg-yellow-500/10" :
                        "bg-red-500/10"
                      }`}>
                        <div className="flex items-center gap-2">
                          <Clock className={`h-4 w-4 ${
                            booking.status === "active" ? "text-green-500" :
                            booking.status === "expiring_soon" ? "text-yellow-500" :
                            "text-red-500"
                          }`} />
                          <span className="text-sm">Restlaufzeit:</span>
                          <span className={`font-bold ${
                            booking.status === "active" ? "text-green-500" :
                            booking.status === "expiring_soon" ? "text-yellow-500" :
                            "text-red-500"
                          }`} data-testid={`days-remaining-${booking.id}`}>
                            {computeLiveDays(booking)} Tage
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Probetraining details */}
                {booking.booking_type === "probetraining" && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Fach</span>
                      <span className="font-medium">{booking.probetraining_subject}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Datum</span>
                      <span className="font-mono">{booking.probetraining_date}</span>
                    </div>
                    <div className={`p-3 rounded-sm ${
                      booking.access_allowed ? "bg-green-500/10" : "bg-secondary/30"
                    }`}>
                      <p className="text-sm">
                        {booking.status === "scheduled" && "Zugriff am gewählten Tag"}
                        {booking.status === "active" && "Zugriff heute verfügbar!"}
                        {booking.status === "expired" && "Probetraining abgelaufen"}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}

          {bookings.length === 0 && (
            <Card className="bg-card border-border col-span-full">
              <CardContent className="py-12 text-center text-muted-foreground">
                <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Keine Buchungen vorhanden</p>
                <p className="text-sm mt-2">Bitte wenden Sie sich an die Verwaltung, um eine Buchung hinzuzufügen.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </ParticipantLayout>
  );
};

export default Dashboard;
